/**
 * TransactionRow Component
 * Single row in the transaction review table
 */
import { useMemo } from 'react';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import StatusBadge from './StatusBadge';
import { CategorySelector } from '@/components/documents/CategorySelector';
import type { TransactionObservation } from '@/types/document';
import type { CategoryType } from '@/types';

export interface TransactionRowProps {
  observation: TransactionObservation;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onEdit: () => void;
  onViewDuplicate: () => void;
  /** Staged import ID for category updates */
  stagedImportId: string;
  /** Callback when observation is updated (for optimistic UI) */
  onObservationUpdated?: () => void;
}

export function TransactionRow({
  observation,
  selected,
  onSelect,
  onEdit,
  onViewDuplicate,
  stagedImportId,
  onObservationUpdated,
}: TransactionRowProps) {
  const isDuplicate = observation.matchStatus === 'duplicate';
  const needsReview = observation.matchStatus === 'needs_review';
  const amount = observation.observedAmount;
  const isNegative = amount < 0;

  // Determine category type from transaction amount for category filtering
  // Positive = income, Negative = expense
  const categoryType: CategoryType | undefined = useMemo(() => {
    if (amount === 0) return undefined;
    return amount >= 0 ? 'income' : 'expense';
  }, [amount]);

  return (
    <tr
      className={cn(
        'hover:bg-gray-50 transition-colors',
        selected && 'bg-primary-50',
        needsReview && 'bg-yellow-50',
        isDuplicate && !selected && 'bg-amber-50/50'
      )}
    >
      {/* Checkbox */}
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          aria-label={`Select transaction: ${observation.observedDescription}`}
        />
      </td>

      {/* Date */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {formatDate(observation.observedDate, { month: 'short', day: 'numeric' })}
        </span>
      </td>

      {/* Amount */}
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <span
          className={cn(
            'text-sm font-medium',
            isNegative ? 'text-red-600' : 'text-green-600'
          )}
        >
          {formatCurrency(amount)}
        </span>
      </td>

      {/* Description */}
      <td className="px-4 py-3">
        <div className="max-w-xs">
          <p className="text-sm text-gray-900 truncate">{observation.observedDescription}</p>
          {/* Show matched transaction info for duplicates */}
          {isDuplicate && observation.matchedTransaction && (
            <p className="mt-1 text-xs text-gray-500">
              Matches: {observation.matchedTransaction.description} (
              {formatDate(observation.matchedTransaction.date, { month: 'short', day: 'numeric' })})
            </p>
          )}
          {/* Show warning for items needing review */}
          {needsReview && (
            <p className="mt-1 text-xs text-yellow-600">
              This transaction may need verification
            </p>
          )}
        </div>
      </td>

      {/* Category (WP-DOC-26) */}
      <td className="px-4 py-3">
        <CategorySelector
          observationId={observation.id}
          stagedImportId={stagedImportId}
          currentCategoryId={observation.effectiveCategoryId}
          currentCategoryName={observation.effectiveCategoryName}
          suggestedCategoryId={observation.suggestedCategoryId}
          suggestedCategoryName={observation.suggestedCategoryName}
          categoryConfidence={observation.categoryConfidence}
          isUserOverride={observation.isUserOverride}
          categoryType={categoryType}
          onCategoryChange={() => {
            // Trigger refresh after category change
            onObservationUpdated?.();
          }}
        />
      </td>

      {/* Status */}
      <td className="px-4 py-3 text-center">
        <StatusBadge
          status={observation.matchStatus}
          confidence={observation.matchConfidence ?? undefined}
        />
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {/* WP-DOC-30: Show View button for both duplicate and needs_review when matchedTransaction exists */}
          {(isDuplicate || needsReview) && observation.matchedTransaction && (
            <button
              type="button"
              onClick={onViewDuplicate}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              View
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Edit
          </button>
        </div>
      </td>
    </tr>
  );
}

export default TransactionRow;
